import { useEffect, useRef } from 'react';
import ui from '../../components/ui.module.css';
import { client } from '../content/client';
import { useHydrated } from '../lib/useHydrated';
import s from '../site.module.css';

/**
 * Denamico's HubSpot meetings scheduler, embedded. It is the iframe HubSpot's MeetingsEmbedCode.js would build, made
 * here instead so the public pages load no third-party script (the site CSP only allows it as a frame):
 *   <iframe src="https://meetings.hubspot.com/<slug>?embed=true&parentHubspotUtk=…&parentPageUrl=…">
 * The scheduler posts { height } as its content grows; the iframe follows it. The prerendered HTML (and a visitor
 * without JavaScript) gets a link to the booking page instead; the iframe replaces it after hydration.
 */

export const MEETINGS_ORIGIN = 'https://meetings.hubspot.com';

/** HubSpot's visitor cookie, when the tracking code has set one (it is off by default). */
export function hubspotUtk(cookie: string): string | undefined {
  return /(?:^|;\s*)hubspotutk=([^;]+)/.exec(cookie)?.[1];
}

/** The scheduler's embed URL, with the visitor cookie and the page it sits on, as HubSpot's embed script adds them. */
export function meetingsSrc(href: string, page: { origin: string; pathname: string }, utk?: string): string {
  const url = new URL(href);
  url.searchParams.set('embed', 'true');
  if (utk) url.searchParams.set('parentHubspotUtk', utk);
  url.searchParams.set('parentPageUrl', `${page.origin}${page.pathname}`);
  return url.toString();
}

export function BookMeeting() {
  const m = client.contact.meetings;
  const hydrated = useHydrated();
  const frame = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!hydrated) return;
    const onMessage = (e: MessageEvent) => {
      const el = frame.current;
      if (!el || e.origin !== MEETINGS_ORIGIN || e.source !== el.contentWindow) return;
      const height = Number((e.data as { height?: unknown } | null)?.height);
      if (height > 0) el.style.height = `${height}px`;
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [hydrated]);

  if (!hydrated) {
    return (
      <p>
        <a className={`${ui.button} ${ui.primary}`} href={m.href} target="_blank" rel="noopener">
          {m.open}
        </a>
      </p>
    );
  }

  return (
    <div className={s.stack}>
      <iframe
        ref={frame}
        className={s.meetings}
        title={m.title}
        src={meetingsSrc(m.href, window.location, hubspotUtk(document.cookie))}
      />
      <p className={s.small}>
        {m.fallbackBefore}
        <a href={m.href} target="_blank" rel="noopener">
          {m.fallbackLabel}
        </a>
        .
      </p>
    </div>
  );
}
